import {
  Box,
  BoxProps,
  Fade,
  Flex,
  FlexProps,
  Image,
  Link,
  useMediaQuery,
} from '@chakra-ui/react';
import {
  ReactEventHandler,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { SOCIAL_PLATFORMS } from 'ui-components';
import { getAsset, getAssetDimensions } from '~/contentSearchUtils';
import { getMainPlatformData } from '~/core/utils';
import { ASSET_TYPE, ISearchContentResult } from '~/searchApi/content';
import { convertPxToRems } from '~/utils-ts';

import { FallbackImage } from '../FallbackImage';
import { ContentCardPlatformIcon } from './components';

export type ContentCardProps = Omit<BoxProps, 'onClick'> & {
  contentData: ISearchContentResult;
  displayCreatorName?: boolean;
  displayPlatform?: boolean;
  FadeComponent?: JSX.Element;
  fallbackImageProps?: FlexProps;
  isSelected?: boolean;
  onClick?: (contentData: ISearchContentResult) => void;
  trackId?: string;
};

export const ContentCard: React.FC<ContentCardProps> = memo(
  ({
    contentData,
    displayCreatorName = true,
    displayPlatform = true,
    FadeComponent,
    fallbackImageProps,
    isSelected = false,
    maxHeight = 260,
    onClick,
    ...props
  }) => {
    const [error, setError] = useState(false);
    const [isHover, setIsHover] = useState(false);
    const { caption, platform, creator } = contentData;
    const { name, handle } = getMainPlatformData(creator);
    const asset = getAsset(contentData) as {
      previewUrl?: string;
      url?: string;
    };
    const previewUrl = asset.previewUrl;
    const url = asset.url || '';
    const ref = useRef<HTMLImageElement>(null);

    const [isTouchable] = useMediaQuery('all and (hover: none)', {
      ssr: false,
    });

    const { ratio } = getAssetDimensions(contentData);
    const isYouTube = platform === SOCIAL_PLATFORMS.youtube;

    const getFallbackWidth = (contentData: ISearchContentResult) => {
      switch (contentData.platform) {
        case SOCIAL_PLATFORMS.tiktok:
          return '147px';
        case SOCIAL_PLATFORMS.instagram:
          return contentData.type === ASSET_TYPE.video ? '146px' : '265px';
      }
    };

    const handleClick = useCallback(
      () => onClick?.(contentData),
      [contentData, onClick],
    );
    const handleError = useCallback(() => {
      setError(true);
    }, []);

    const handleHover = useCallback((value: boolean) => setIsHover(value), []);

    const handleLoad: ReactEventHandler<HTMLImageElement> = (event) => {
      const imageElement = event.target as HTMLImageElement;
      imageElement.style.removeProperty('aspect-ratio');
    };

    useEffect(() => {
      if (!ref.current?.src) {
        setError(true);
      }
    }, [ref]);

    return (
      <Box
        cursor="pointer"
        flexGrow={1}
        height={maxHeight}
        marginBottom={6}
        onClick={handleClick}
        onMouseEnter={() => handleHover(true)}
        onMouseLeave={() => handleHover(false)}
        position="relative"
        {...props}
      >
        {error ? (
          <FallbackImage
            minWidth={getFallbackWidth(contentData)}
            boxSize="100%"
            borderRadius="lg"
            trackId="foam-content-preview"
            {...fallbackImageProps}
          />
        ) : (
          <Flex>
            <Image
              ref={ref}
              onError={handleError}
              alt={caption}
              background="white"
              borderRadius="lg"
              src={previewUrl ?? url}
              onLoad={handleLoad}
              track-id="foam-content-preview"
              sx={{
                height: maxHeight,
                objectFit: 'cover',
                maxWidth: '100%',
                minWidth: '100%',
                verticalAlign: 'bottom',
                aspectRatio: ratio,
              }}
            />
            <Flex
              alignItems="center"
              gap={1}
              position="absolute"
              top="85%"
              paddingY={convertPxToRems(10)}
              paddingX={2}
              background="rgba(255, 255, 255, 0.75)"
              height={10}
              width="100%"
              justifyContent={'space-between'}
              display={isHover || isTouchable ? 'flex' : 'none'}
              transition="all 0.5s ease-in"
            >
              {displayCreatorName && (
                <Link
                  _hover={{ textDecoration: 'none' }}
                  fontSize="sm"
                  fontWeight="medium"
                  lineHeight={convertPxToRems(14)}
                  noOfLines={1}
                  overflow="hidden"
                  textOverflow="ellipsis"
                  track-id="foam-content-creatorName"
                >
                  {name ?? handle}
                </Link>
              )}
              {displayPlatform && (
                <ContentCardPlatformIcon
                  height={isYouTube ? 8 : 6}
                  width={isYouTube ? 8 : 6}
                  platform={platform}
                  trackId="foam-content-platformIcon"
                />
              )}
            </Flex>
          </Flex>
        )}

        <Fade in={isHover || isSelected}>
          <Box
            background="linear-gradient(0deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4))"
            borderTopRadius="lg"
            height="calc(100% - 40px)"
            left={0}
            position="absolute"
            top={0}
            width="100%"
          >
            {FadeComponent}
          </Box>
        </Fade>
      </Box>
    );
  },
);
